// add-named-default / drop-named-default edit planner (v2 Slice B5).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B5.
//
// Both ops append a fresh statement after the source statement (DSE2-7: no
// coalescing by default; opt-in coalescing is slice H1). The synthesizer
// produces either an `ALTER TABLE … ADD CONSTRAINT name DEFAULT (expr) FOR
// col` (inline) or `CREATE DEFAULT name AS expr` / `DROP DEFAULT name`
// (standalone) statement. The standalone form does not require a CREATE/
// ALTER TABLE source — it can append after any DDL.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { QualifiedName } from '../../../sql-grammar/model';
import type { DdlEdit, DdlStructure, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAddNamedDefault,
  synthesizeDropNamedDefault,
} from './dialect.namedDefault';

type NamedDefaultEdit = Extract<
  DdlEdit,
  { op: 'add-named-default' | 'drop-named-default' }
>;

export function planNamedDefaultEdit(
  ctx: DdlEditCtx,
  edit: NamedDefaultEdit,
): readonly PlannedByteEdit[] {
  const table = pickTable(ctx.structure);
  const synth = edit.op === 'add-named-default'
    ? synthesizeAddNamedDefault(ctx.engine, table, edit.namedDefault)
    : synthesizeDropNamedDefault(ctx.engine, table, edit.constraintName, edit.standalone);

  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: `${edit.op}:append`,
  }];
}

function pickTable(s: DdlStructure): QualifiedName | undefined {
  if (s.kind === 'alter-table') return s.table;
  if (s.kind === 'create-table') return s.table;
  return undefined;
}
